import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Typography } from '@beeline/design-system-react/extracted-styles';
import { ImageMessage } from '@beeline/lk-ui';

import styles from './styles.module.scss';

import forbiddenErrorImage from ':assets/images/errors/403.png';
import forbiddenErrorImage2x from ':assets/images/errors/403@2x.png';
import forbiddenErrorImage3x from ':assets/images/errors/403@3x.png';
import notFoundImage from ':assets/images/errors/404.png';
import unknownErrorImage from ':assets/images/errors/gear.webp';
import unknownErrorImage2x from ':assets/images/errors/gear@2x.webp';
import unknownErrorImage3x from ':assets/images/errors/gear@3x.webp';
import networkErrorImage from ':assets/images/errors/internal_network_error.png';
import networkErrorImage2x from ':assets/images/errors/internal_network_error@2x.png';
import networkErrorImage3x from ':assets/images/errors/internal_network_error@3x.png';
import {
  BrowserTitle,
  AnalyticsTrackPageView,
  WithHDButtons,
} from ':components';

export type ErrorPageErrorType =
  | 'notFound'
  | 'internalNetwork'
  | 'forbidden'
  | 'unknown';

interface Props {
  errorType: ErrorPageErrorType;
  url: string | undefined;
  isHDWidgetForService: boolean;
}

export function ErrorPage({
  errorType,
  url,
  isHDWidgetForService,
}: Props): ReactElement {
  const navigate = useNavigate();
  const handleNavigate = (): void => {
    navigate('/');
  };

  const errorTitles = {
    notFound: 'Страница не найдена',
    internalNetwork: 'Сервис недоступен из внешней сети',
    forbidden: 'Страница недоступна',
    unknown: 'Что-то пошло не так',
  };

  const errorDescriptions = {
    notFound: 'Возможно, она была перемещена или неверно указан адрес страницы',
    internalNetwork: (
      <Typography
        variant='body2'
        className={styles.internalNetworkErrorDescription}
      >
        Для работы необходимо подключиться к внутренней сети
        <br />
        (Например, VPN, VDI или Wi-Fi Employee в офисе)
      </Typography>
    ),
    forbidden: (
      <>Возможно, у&nbsp;тебя нет прав на&nbsp;доступ к&nbsp;этому сервису</>
    ),
    unknown: 'Произошла непредвиденная ошибка, попробуй обновить страницу',
  };

  const errorImages = {
    notFound: {
      src: notFoundImage,
      srcset: `${notFoundImage} 3x`,
    },
    internalNetwork: {
      src: networkErrorImage,
      srcset: `${networkErrorImage2x} 2x, ${networkErrorImage3x} 3x`,
    },
    forbidden: {
      src: forbiddenErrorImage,
      srcset: `${forbiddenErrorImage2x} 2x, ${forbiddenErrorImage3x} 3x`,
    },
    unknown: {
      src: unknownErrorImage,
      srcset: `${unknownErrorImage2x} 2x, ${unknownErrorImage3x} 3x`,
    },
  };

  const errorPrimaryButtons = {
    notFound: {
      title: 'Перейти на главную',
      onClick: handleNavigate,
    },
    internalNetwork: {
      title: 'Обновить страницу',
      onClick: () => {
        window.location.reload();
      },
    },
    forbidden: {
      title: 'Перейти на главную',
      onClick: handleNavigate,
    },
    unknown: {
      title: 'Обновить страницу',
      onClick: () => {
        window.location.reload();
      },
    },
  };

  const errorSecondaryButtons = {
    notFound: undefined,
    internalNetwork: {
      title: 'Способы подключения',
      href: 'https://space.beeline.ru/Regions/it/Pages/page_id7211_field0.aspx',
      target: '_blank',
    },
    forbidden: undefined,
    unknown: undefined,
  };
  return (
    <WithHDButtons serviceURLName={isHDWidgetForService ? url : undefined}>
      <>
        <BrowserTitle title={errorTitles[errorType]} />
        <AnalyticsTrackPageView />
        <ImageMessage
          title={errorTitles[errorType]}
          description={errorDescriptions[errorType]}
          PrimaryButton={errorPrimaryButtons[errorType]}
          SecondaryButton={errorSecondaryButtons[errorType]}
          imageProps={errorImages[errorType]}
          imageSize='large'
        />
      </>
    </WithHDButtons>
  );
}
